/**
 * Vertex AI Configuration Section Component
 *
 * Inline expandable card for managing Vertex AI / Gemini connections.
 * Used in the full-page setup flow (IntegrationSetupPage).
 */

import { useState } from "react";
import {
  Plus,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { VertexConnectionsList } from "./VertexConnectionsList";
import { VertexSetupInstructions } from "./VertexSetupInstructions";
import { useCreateVertexConnection, useVertexConnections } from "@/hooks/useVertex";
import { GoogleCloudLogo } from "./logos";
import type { VertexConnectionCreate } from "@/types/vertex";

const GCP_REGIONS = [
  "us-central1",
  "us-east1",
  "us-east4",
  "us-west1",
  "us-west4",
  "europe-west1",
  "europe-west2",
  "europe-west4",
  "asia-east1",
  "asia-northeast1",
  "asia-southeast1",
  "australia-southeast1",
];

interface VertexConfigSectionProps {
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  onConnectionCreated?: () => void;
}

export function VertexConfigSection({
  expanded = false,
  onExpandChange,
  onConnectionCreated,
}: VertexConfigSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [useAdc, setUseAdc] = useState(false);
  const [formData, setFormData] = useState<VertexConnectionCreate>({
    connection_name: "",
    gcp_project_id: "",
    gcp_region: "us-central1",
    bigquery_dataset: "",
    service_account_key: "",
    use_adc: false,
  });

  const { data: connectionsData } = useVertexConnections();
  const createMutation = useCreateVertexConnection();

  const connectionCount = connectionsData?.data.length ?? 0;
  const hasConnections = connectionCount > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: VertexConnectionCreate = {
      connection_name: formData.connection_name,
      gcp_project_id: formData.gcp_project_id,
      gcp_region: formData.gcp_region,
      bigquery_dataset: formData.bigquery_dataset,
    };
    if (useAdc) {
      payload.use_adc = true;
    } else {
      payload.service_account_key = formData.service_account_key;
    }
    try {
      await createMutation.mutateAsync(payload);
      setShowAddForm(false);
      setUseAdc(false);
      setFormData({
        connection_name: "",
        gcp_project_id: "",
        gcp_region: "us-central1",
        bigquery_dataset: "",
        service_account_key: "",
        use_adc: false,
      });
      onConnectionCreated?.();
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => onExpandChange?.(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
            <GoogleCloudLogo className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">
                Gemini Enterprise Agent Platform
              </CardTitle>
              {hasConnections && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
                  <CheckCircle2 className="h-3 w-3" />
                  {connectionCount} connected
                </span>
              )}
            </div>
            <CardDescription className="text-sm mt-1">
              Monitor AI agents running on Google's Gemini Enterprise Agent Platform via
              BigQuery Observability Analytics
            </CardDescription>
          </div>
          <div className="flex items-center">
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          <Separator className="mb-6" />

          <VertexConnectionsList />

          {!showAddForm ? (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => setShowAddForm(true)}
                className="w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Connection
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-6">
              <Separator />

              <div className="flex items-center justify-between">
                <h4 className="font-medium">New Vertex AI Connection</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowInstructions(!showInstructions)}
                >
                  {showInstructions ? "Hide" : "Show"} Setup Guide
                </Button>
              </div>

              {showInstructions && (
                <VertexSetupInstructions isLoading={false} />
              )}

              <Separator />

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vertex-connection-name">Connection Name</Label>
                  <Input
                    id="vertex-connection-name"
                    placeholder="e.g. prod-vertex"
                    value={formData.connection_name}
                    onChange={(e) =>
                      setFormData({ ...formData, connection_name: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vertex-project-id">GCP Project ID</Label>
                    <Input
                      id="vertex-project-id"
                      placeholder="e.g. my-gcp-project"
                      value={formData.gcp_project_id}
                      onChange={(e) =>
                        setFormData({ ...formData, gcp_project_id: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="vertex-region">GCP Region</Label>
                    <Select
                      value={formData.gcp_region}
                      onValueChange={(value) =>
                        setFormData({ ...formData, gcp_region: value })
                      }
                    >
                      <SelectTrigger id="vertex-region">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        {GCP_REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vertex-dataset">BigQuery Dataset</Label>
                  <Input
                    id="vertex-dataset"
                    placeholder="e.g. observability_genai_spans"
                    value={formData.bigquery_dataset}
                    onChange={(e) =>
                      setFormData({ ...formData, bigquery_dataset: e.target.value })
                    }
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    The BigQuery dataset linked to Observability Analytics
                  </p>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="vertex-adc-toggle">Use Application Default Credentials</Label>
                    <p className="text-xs text-muted-foreground">
                      Use the platform's service account instead of a separate key.
                      Requires BigQuery Data Viewer and Job User roles on the service account.
                    </p>
                  </div>
                  <Switch
                    id="vertex-adc-toggle"
                    checked={useAdc}
                    onCheckedChange={setUseAdc}
                  />
                </div>

                {!useAdc && (
                  <div className="space-y-2">
                    <Label htmlFor="vertex-sa-key">Service Account Key (JSON)</Label>
                    <Textarea
                      id="vertex-sa-key"
                      placeholder='{"type": "service_account", "project_id": "...", ...}'
                      value={formData.service_account_key}
                      onChange={(e) =>
                        setFormData({ ...formData, service_account_key: e.target.value })
                      }
                      required
                      rows={6}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      Paste the full JSON key content. It will be encrypted at rest.
                    </p>
                  </div>
                )}

                {createMutation.isError && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      {createMutation.error instanceof Error
                        ? createMutation.error.message
                        : "Failed to create connection"}
                    </span>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Validating & Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="mr-2 h-4 w-4" />
                        Create Connection
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                    disabled={createMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
